import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { getAdSessionId } from "../lib/adAttribution";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser, checkAuth } = useAuth();
  const ran = React.useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");
    if (!sessionId) { navigate("/"); return; }

    // Hard fallback — if we're still on this page after 45s, the user
    // is truly stuck (backend stalled, OAuth provider down). Redirect
    // home so they never see "Aligning the stars" forever.
    const fallbackTimer = setTimeout(() => {
      toast.error("Sign-in is taking longer than usual. Please try again.", { duration: 6000 });
      navigate("/", { replace: true });
    }, 45000);

    (async () => {
      try {
        // Clear the session_id hash early — this matters because App.js
        // routes to <AuthCallback /> based on URL hash. If we leave the
        // hash in place and something re-renders, this effect could be
        // re-triggered (or the user could bookmark a stale auth URL).
        try { window.history.replaceState(null, "", window.location.pathname + window.location.search); } catch (_) {}

        // 32s frontend timeout — backend has a 20s upstream timeout +
        // ~3-5s of DB writes (customer insert, session insert, cookie
        // set, daily-bonus calc). 32s leaves comfortable headroom even
        // for the worst cold-start case.
        const { data } = await api.post(
          "/auth/session",
          { session_id: sessionId, ad_session_id: getAdSessionId() },
          { timeout: 32000 },
        );
        // The /auth/session response already contains the full user —
        // skip the redundant /auth/me round-trip that was previously
        // blocking navigation. checkAuth() runs in the background to
        // pick up daily_bonus / vip flags without holding up the UI.
        if (data?.user) {
          try { setUser(data.user); } catch (_) {}
        }
        checkAuth().catch(() => { /* non-fatal: dashboard will refresh */ });
        // Flag the welcome bonus so the dashboard can show a celebratory popup
        if (data?.is_new_user) {
          try {
            sessionStorage.setItem(
              "ak_welcome_bonus",
              JSON.stringify({ credit: data.welcome_credit || 100, at: Date.now() }),
            );
          } catch (_) { /* ignore quota */ }
        }
        clearTimeout(fallbackTimer);
        // The OAuth provider already returned us to the page the user was
        // on when they clicked sign-in (the `redirect` URL). The session_id
        // arrived in the URL hash, which we stripped above — so the current
        // pathname IS the intended landing page (e.g. /astrologers/sai-baba).
        //
        // For NEW users we append `?signup=success` so Google Ads can detect
        // the sign-up conversion via a URL-pattern rule. Returning users land
        // on the same path without the marker (no conversion fired).
        const landing = (window.location.pathname || "/")
          + (window.location.search || "");
        const cleanLanding = landing === "/" || landing.startsWith("/?") ? "/astrologers" + landing.slice(1) : landing;
        if (data?.is_new_user) {
          const sep = cleanLanding.includes("?") ? "&" : "?";
          navigate(`${cleanLanding}${sep}signup=success`, { replace: true });
        } else {
          navigate(cleanLanding, { replace: true });
        }
      } catch (e) {
        clearTimeout(fallbackTimer);
        // eslint-disable-next-line no-console
        console.error("Auth callback failed:", e?.response?.status, e?.message);
        const status = e?.response?.status;
        if (status === 504 || e?.code === "ECONNABORTED") {
          toast.error("Sign-in is taking too long. Our auth provider is slow — please try again in a moment.", { duration: 7000 });
        } else if (status === 401) {
          toast.error("Sign-in link expired. Please try signing in again.", { duration: 6000 });
        } else {
          toast.error("Sign-in failed. Please try again.", { duration: 6000 });
        }
        navigate("/", { replace: true });
      }
    })();

    return () => clearTimeout(fallbackTimer);
  }, [navigate, setUser, checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="auth-callback">
      <div className="text-center">
        <div className="font-serif-display text-2xl text-gold">Aligning the stars...</div>
        <div className="mt-3 text-xs text-muted">If this takes more than 45 seconds, we'll bring you back home automatically.</div>
      </div>
    </div>
  );
}
